/**
 * Client-side queue ETA — mirrors server `annotateQueueWithEta`.
 */
import type { Track } from "@memes/shared";
import { isOwnerActive, type ActiveOwnersMap } from "./shuffleActiveOwners";

/** Seconds until queue index `i` starts; null if already played or unknown. */
export function timeUntilIndex(
  i: number,
  queue: Track[],
  currentIndex: number,
  currentTime: number,
  activeOwners: ActiveOwnersMap = {},
): number | null {
  if (i < currentIndex) return null;
  const entries = queue.map((t, index) => ({
    index,
    owner: t.owner,
    durationSec: t.durationSec ?? null,
  }));
  return computeEta(entries, currentIndex, currentTime, i, activeOwners);
}

function computeEta(
  entries: Array<{ index: number; owner: string; durationSec: number | null }>,
  currentIndex: number,
  currentTime: number,
  targetIndex: number,
  activeOwners: ActiveOwnersMap,
): number | null {
  const c = entries[targetIndex];
  if (!c || c.index < currentIndex) return null;

  const currentEntry = entries[currentIndex];
  if (!currentEntry) return null;

  if (c.index === currentIndex) {
    if (!isOwnerActive(activeOwners, currentEntry.owner)) return 0;
    return c.durationSec != null ? Math.max(0, c.durationSec - currentTime) : null;
  }

  if (!isOwnerActive(activeOwners, c.owner)) {
    return null;
  }

  let total = 0;
  let ok = true;

  if (isOwnerActive(activeOwners, currentEntry.owner)) {
    if (currentEntry.durationSec == null) return null;
    total = Math.max(0, currentEntry.durationSec - currentTime);
  }

  for (let j = currentIndex + 1; j < c.index && ok; j++) {
    const interm = entries[j];
    if (!interm) continue;
    if (!isOwnerActive(activeOwners, interm.owner)) continue;
    if (interm.durationSec != null) total += interm.durationSec;
    else ok = false;
  }
  return ok ? total : null;
}

/** Label for the ETA column. */
export function formatQueueEta(
  i: number,
  queue: Track[],
  currentIndex: number,
  currentTime: number,
  isPlaying: boolean,
  activeOwners: ActiveOwnersMap = {},
): string {
  if (i < currentIndex) return "—";
  const eta = timeUntilIndex(i, queue, currentIndex, currentTime, activeOwners);
  if (eta == null) return "—";
  if (i === currentIndex) {
    if (!isPlaying) return "Paused";
    return eta <= 0 ? "Now" : formatSeconds(eta);
  }
  return formatSeconds(eta);
}

function formatSeconds(sec: number): string {
  const total = Math.floor(sec);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}
